import React from "react";

import dayjs from "dayjs";
import customParseFormat from 'dayjs/plugin/customParseFormat'

import Typography from "@mui/joy/Typography";
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Box from "@mui/joy/Box";
import {CardActions, CircularProgress, Stack} from "@mui/joy";
import Link from "@mui/joy/Link";

// components
import CommentRead from "./CommentRead";
import CommentLike from "./CommentLike";
import CommentFav from "./CommentFav";
import CommentDelete from "./CommentDelete";
import BaseApi from "../../utils/BaseApi";
import CommentReplyCard from "./CommentReplyCard";
import CommentAttachment from "./CommentAttachment";
import ProfileHorizontalByProps from "../Profile/ProfileHorizontalByProps";
import UserSubscriberContext from "../../context/UserSubscriberContext";
import CommonEditModal from "../Common/CommonEditModal";
import CommentReplyModal from "./CommentReplyModal";

// icons
import ReplyIcon from '@mui/icons-material/Reply';
import Chip from "@mui/joy/Chip";

import {
    replaceContentOptimized,
} from "./utils/replaceContentOptimized";

dayjs.extend(customParseFormat)


class CommentItem extends React.Component {
    static contextType = UserSubscriberContext

    constructor(props) {
        super(props);
        this.state = {
            comment: null,
            open: false,
            readed: false,
        };
    }

    async componentDidMount() {
        if (this.props.comment.content === undefined) {
            await this.retrieveComment();
        } else {
            try {
                const processedComment = { ...this.props.comment };
                processedComment.content = await replaceContentOptimized(this.props.comment.content);

                this.setState({
                    comment: processedComment,
                    readed: this.props.comment.read_by?.map(e => e.id).includes(this.context.userObject.id)
                });
            } catch (error) {
                console.error('Error processing comment:', error);
            }
        }
    }

    async retrieveComment() {
        const url = `comment/${this.props.comment.id}/`;
        const params = {};
        const api = new BaseApi();
        let response = await api.get(url, params);
        let comment = response.data;
        comment.original_content = comment.content;
        comment.content = await replaceContentOptimized(comment.content);

        this.setState({
            comment: comment,
            readed: response.data?.read_by?.map(e => e.id).includes(this.context.userObject.id)
        });
    }

    reply(){
        this.props.replyCallBack(this.state.comment)
    }

    switchOpen(){
        this.setState({open: !this.state.open})
    }

    setReaded(){
        this.setState({readed: true})
    }

    render() {
        if (!this.state.comment){
            return (<CircularProgress/>)
        }
        
        const comment = this.state.comment;
        const isEditable = comment.owner?.id === this.context.userObject.id && 
                          Math.abs(new Date() - dayjs(comment.created_on, 'DD.MM.YYYY HH:mm:ss').toDate()) < 100000;
        const isUnread = !this.state.readed;
        
        return (
            <Card
                sx={{
                    width: '100%',
                    backgroundColor: this.props.favorite ? 'warning.softBg' : 
                                  isUnread ? 'purple.softBg' : 'neutral.softBg',
                    color: isUnread ? 'white' : 'inherit',
                }}
                size={'sm'}
                color={this.props.favorite ? 'warning' : isUnread ? 'purple' : 'neutral'}
                variant={'soft'}
                id={'CommentItemCard' + comment.id}
            >
                <CardContent
                    sx={{
                        overflowY: 'scroll',
                        width: '100%',
                    }}
                >
                    <Stack
                        direction={'row'}
                        spacing={1}
                        useFlexGap
                        sx={{
                            mb: 1,
                            flexWrap: 'wrap',
                        }}
                        alignItems={'center'}
                    >
                        <ProfileHorizontalByProps
                            user={comment.owner}
                        />
                        <Typography
                            level="body-sm"
                        >
                            {comment.created_on}
                        </Typography>
                        {
                            comment.xtype !== 'COMMON'?
                                <Chip
                                    size={'sm'}
                                    color={'primary'}
                                >
                                    #{comment.xtype_human}
                                </Chip>:
                                null
                        }
                        {
                            isEditable ?
                                <CommonEditModal
                                    fields={['content']}
                                    flat={true}
                                    baseUrl={'comment'}
                                    obj={{
                                        id: comment.id,
                                        content: comment.original_content
                                    }}
                                    callBack={this.retrieveComment.bind(this)}
                                />:
                                null
                        }
                        {
                            this.props.replyCallBack ?
                                <ReplyIcon
                                    fontSize={'small'}
                                    color={'neutral'}
                                    sx={{
                                        ml: 1,
                                        "&:hover": {
                                            cursor: 'pointer',
                                        }
                                    }}
                                    onClick={this.reply.bind(this)}
                                />:
                                null
                        }
                        {
                            this.props.inlineReply ?
                                <CommentReplyModal
                                    appName={comment.related_app}
                                    appId={comment.related_app_id}
                                    replyTo={comment}
                                    callBack={this.props.addCommentCallBack}
                                />:
                                null
                        }
                    </Stack>
                    {
                        comment.reply_to ?
                            <CommentReplyCard
                                comment={comment.reply_to}
                            />:
                            null
                    }
                    <Box
                        sx={{
                            fontSize: '0.8rem',
                            ...this.props.withCut ? {
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                height: this.state.open ? null : '46px',
                            } : {},
                        }}
                    >
                        <div
                            className={'commentItem'}
                            dangerouslySetInnerHTML={{__html: comment.content}}
                            style={{width: '100%', overflowY: 'scroll'}}
                        />
                    </Box>
                    {
                        this.props.withCut ?
                            this.state.open ?
                                <Link onClick={this.switchOpen.bind(this)} level="body-sm">
                                    Свернуть
                                </Link> :
                                <Link onClick={this.switchOpen.bind(this)} level="body-sm">
                                    Смотреть полностью
                                </Link> :
                            null
                    }
                    {
                        comment.attachments && comment.attachments.length > 0 ?
                            <Stack
                                spacing={1}
                                direction={'row'}
                                sx={{ flexWrap: 'wrap' }}
                            >
                                {comment.attachments.map((a) => (
                                    <CommentAttachment
                                        key={'attachment-' + a}
                                        id={a}
                                        link={true}
                                    />
                                ))}
                            </Stack> :
                            null
                    }
                </CardContent>
                <CardActions>
                    <Stack
                        direction={'row'}
                        spacing={1}
                        alignItems={'center'}
                    >
                        <CommentRead
                            key={'CommentRead' + comment.id}
                            comment={comment}
                            callBack={this.setReaded.bind(this)}
                        />
                        <CommentLike
                            key={'CommentLike' + comment.id}
                            liked={comment.liked}
                            commentID={comment.id}
                        />
                        <CommentFav
                            key={'CommentFav' + comment.id}
                            comment={comment}
                            callBack={this.props.callBack}
                        />
                        <CommentDelete
                            key={'CommentDelete' + comment.id}
                            comment={comment}
                            callBack={this.props.callBack}
                        />
                    </Stack>
                </CardActions>
            </Card>
        );
    }
}

export default CommentItem;
